export type PropertyType =
  | "Flat"
  | "Plot"
  | "Villa"
  | "Commercial"
  | "Penthouse"
  | "Row_House";

export type UnitStatus =
  | "Available"
  | "On_Hold"
  | "Token_Received"
  | "Booked"
  | "Sold";

export type SiteVisitStatus =
  | "Scheduled"
  | "Completed"
  | "Cancelled"
  | "No_Show";

export type LeadSource =
  | "99acres"
  | "MagicBricks"
  | "Housing.com"
  | "NoBroker"
  | "Meta_Ads"
  | "Google_Ads"
  | "Walk_In"
  | "Channel_Partner"
  | "Website";

export type PaymentPlan =
  | "Construction_Linked"
  | "Time_Linked"
  | "Down_Payment";

export interface Project {
  id: string;
  name: string;
  location_address: string;
  city: string;
  google_maps_url?: string;
  rera_number?: string;
  created_at: string;
}

export interface Property {
  id: string;
  project_id: string;
  unit_number: string;
  property_type: PropertyType;
  configuration: string; // e.g., "2BHK", "3BHK", "1200 SqFt Plot"
  floor_number?: number;
  carpet_area_sqft: number;
  super_builtup_sqft?: number;
  base_price: number;
  facing?: string;
  status: UnitStatus;
  hold_until?: string | null;
  /** Sales id of the agent who placed the unit On_Hold (cleared on release). */
  held_by?: string | number | null;
  amenities?: string[];
  created_at: string;
  project?: Project;
}

export interface PropertyMedia {
  id: string;
  property_id: string;
  media_type: "Photo" | "Floorplan" | "Brochure_PDF" | "Video";
  media_url: string;
  created_at: string;
}

export interface ChannelPartner {
  id: string;
  agency_name: string;
  contact_person: string;
  phone: string;
  email?: string;
  rera_number?: string;
  commission_percentage: number;
  created_at: string;
}

export type KioskCheckInResult = {
  ok: boolean;
  contactId?: string | number;
  assignedAgentName: string;
  lockRetained?: boolean;
  cpLockedUntil?: string | null;
  error?: string;
};

/** WhatsApp campaign / drip audience row (seed or mapped from contacts). */
export type CampaignAudienceLead = {
  id: string;
  name: string;
  phone: string;
  budget: string;
  /** Raw budget for numeric campaign filters (₹). */
  budget_min?: number | null;
  budget_max?: number | null;
  preferred_type: string;
  preferred_location: string;
  /** Contact temperature (cold / warm / hot / …) — same values as noteStatuses. */
  status: string;
  last_contacted_days_ago: number;
  agent: string;
  sales_id?: string | number | null;
  has_phone: boolean;
};

/** Manager-editable WhatsApp campaign message templates (stored in configuration). */
export type WhatsAppCampaignTemplate = {
  id: string;
  label: string;
  body: string;
  /** Optional public HTTPS image or PDF URL (Meta Cloud API media_url). */
  media_url?: string;
};

export const DEFAULT_WHATSAPP_CAMPAIGN_TEMPLATES: WhatsAppCampaignTemplate[] = [
  {
    id: "t1",
    label: "Project brochure + floorplans",
    body: "Sharing our latest project brochure and floorplans — reply if you would like a call back.",
  },
  {
    id: "t2",
    label: "Weekend site visit invite",
    body: "Weekend site-visit slots are open — reply with a preferred time and we will confirm.",
  },
  {
    id: "t3",
    label: "Token booking window",
    body: "Token booking window is open this week — reply YES if you want us to hold a unit discussion.",
  },
];

/**
 * Missing/non-array config → seed defaults.
 * Explicit [] or only-invalid rows → [] (do not silently restore defaults).
 */
export function normalizeWhatsAppCampaignTemplates(
  raw: unknown,
): WhatsAppCampaignTemplate[] {
  if (!Array.isArray(raw)) {
    return DEFAULT_WHATSAPP_CAMPAIGN_TEMPLATES.map((t) => ({ ...t }));
  }
  return raw
    .map((row, index) => {
      if (!row || typeof row !== "object") return null;
      const r = row as Record<string, unknown>;
      const label = String(r.label ?? "").trim();
      const body = String(r.body ?? "").trim();
      if (!label || !body) return null;
      const id =
        String(r.id ?? "").trim() || `t-${Date.now().toString(36)}-${index}`;
      const media_url = String(r.media_url ?? "").trim();
      return {
        id,
        label,
        body,
        ...(media_url ? { media_url } : {}),
      };
    })
    .filter((t): t is WhatsAppCampaignTemplate => t != null);
}

/** Max quick-reply presets shown in the contact WhatsApp chat composer. */
export const MAX_WHATSAPP_CHAT_PRESETS = 6;

/** Manager-editable WhatsApp chat quick presets (stored in configuration). */
export type WhatsAppChatPreset = {
  id: string;
  title: string;
  /** Message body. Use `{name}` for the contact's first/display name. */
  body: string;
};

export const DEFAULT_WHATSAPP_CHAT_PRESETS: WhatsAppChatPreset[] = [
  {
    id: "p1",
    title: "Digital Brochure",
    body: "Namaste {name}! Thank you for inquiring about our projects. Reply and we will share the brochure and floorplans.",
  },
  {
    id: "p2",
    title: "Site Visit Invite",
    body: "Namaste {name}! We would love to host you for a site visit. Share a preferred date/time and we will confirm.",
  },
  {
    id: "p3",
    title: "Cost Sheet & Quote",
    body: "Namaste {name}! We can share an estimated cost sheet and payment milestone breakdown for your preferred unit. Which configuration are you considering?",
  },
];

/**
 * Missing/non-array config → seed defaults.
 * Explicit [] or only-invalid rows → [] (do not silently restore defaults).
 * Caps at MAX_WHATSAPP_CHAT_PRESETS.
 */
export function normalizeWhatsAppChatPresets(
  raw: unknown,
): WhatsAppChatPreset[] {
  if (!Array.isArray(raw)) {
    return DEFAULT_WHATSAPP_CHAT_PRESETS.map((t) => ({ ...t }));
  }
  return raw
    .map((row, index) => {
      if (!row || typeof row !== "object") return null;
      const r = row as Record<string, unknown>;
      const title = String(r.title ?? r.label ?? "").trim();
      const body = String(r.body ?? r.text ?? "").trim();
      if (!title || !body) return null;
      const id =
        String(r.id ?? "").trim() || `p-${Date.now().toString(36)}-${index}`;
      return { id, title, body };
    })
    .filter((t): t is WhatsAppChatPreset => t != null)
    .slice(0, MAX_WHATSAPP_CHAT_PRESETS);
}

/** Replace `{name}` placeholders in a chat preset body. */
export function applyWhatsAppChatPresetBody(
  body: string,
  contactName: string,
): string {
  const name = contactName.trim() || "there";
  return body.replaceAll("{name}", name);
}

/** Midpoint (or single bound) of a contact budget, in ₹. */
export function campaignBudgetMidpoint(
  min?: number | null,
  max?: number | null,
): number | null {
  if (min != null && max != null) return (Number(min) + Number(max)) / 2;
  if (min != null) return Number(min);
  if (max != null) return Number(max);
  return null;
}

/** Campaign budget filter keys → inclusive ₹ ranges (max exclusive except 4Cr+). */
export function campaignBudgetFilterMatches(
  filter: string,
  min?: number | null,
  max?: number | null,
): boolean {
  if (filter === "all") return true;
  const mid = campaignBudgetMidpoint(min, max);
  if (mid == null) return false;
  switch (filter) {
    case "1_1.5":
      return mid >= 10_000_000 && mid < 15_000_000;
    case "1.5_2":
      return mid >= 15_000_000 && mid < 20_000_000;
    case "2_4":
      return mid >= 20_000_000 && mid < 40_000_000;
    case "4_plus":
      return mid >= 40_000_000;
    default:
      return true;
  }
}

export type LeadSourceStat = {
  source: string;
  leads: number;
  contacted: number;
  booked: number;
  siteVisits: number;
  /** Token_Received units attributed to this source (not booking count). */
  tokens: number;
  cpl: string;
  conversion: string;
  funnel?: {
    lead: number;
    contacted: number;
    visit: number;
    token: number;
    booked: number;
  };
};

export type PortalSpendBySource = Partial<Record<string, number>>;

export type AgentScorecardRow = {
  agentId: string;
  agentName: string;
  avgFirstTouchMinutes: number | null;
  showRate: number | null;
  completedVisits: number;
  noShowVisits: number;
  bookingsCount: number;
  grossBookingValue: number;
};

export type InventoryAgingRow = {
  propertyId: string;
  unitNumber: string;
  projectName: string;
  status: "Available" | "On_Hold";
  daysInStatus: number;
};

export type AgentLeaderboardRow = {
  name: string;
  calls: number;
  callDuration: string;
  siteVisits: number;
  tokens: number;
  grossValue: number;
};

export type AnalyticsMetrics = {
  totalLeads: number;
  siteVisitsConducted: number;
  tokensBookingsLabel: string;
  grossBookingValueLabel: string;
  usingSeed: boolean;
};

export type StalenessResult = {
  ok: boolean;
  reassigned: number;
  message: string;
};

export type VisitOutcome = "Completed" | "No_Show" | "Reschedule" | "Cancelled";

export type RecordVisitOutcomeOptions = {
  feedback?: string;
  /** Required when outcome is Reschedule */
  scheduled_at?: string;
};

export type RecordVisitOutcomeResult = {
  ok: boolean;
  error?: string;
};

export type SendDueVisitRemindersResult = {
  sent: number;
};

export type SalesAgentOption = {
  id: string | number;
  name: string;
};

/** Per-partner lead attribution + active 30-day lock counts (from contacts). */
export type ChannelPartnerLeadStat = {
  partnerId: string;
  leadCount: number;
  lockedLeadCount: number;
  nextLockExpiry?: string | null;
};

export interface SiteVisit {
  id: string;
  contact_id: string;
  property_id?: string;
  agent_id?: string;
  scheduled_at: string;
  status: SiteVisitStatus;
  feedback_notes?: string;
  site_photos?: string[];
  created_at: string;
  property?: Property;
}

export interface CallLog {
  id: string;
  contact_id: string;
  agent_id?: string;
  call_duration_seconds: number;
  call_disposition: "Connected" | "Busy" | "Unreachable" | "Wrong Number";
  recording_url?: string;
  created_at: string;
}

export interface WhatsAppMessage {
  id: string;
  contact_id?: string | number;
  direction: "INBOUND" | "OUTBOUND";
  message_body: string;
  media_url?: string;
  status: string;
  created_at: string;
}

export type DiscountStatus = "none" | "pending" | "approved" | "rejected";
export type PayoutStatus = "pending" | "paid";

export type AgentCommissionRule =
  | { type: "percent"; rate: number }
  | { type: "slab"; slabs: Array<{ minAgreementValue: number; rate: number }> };

export type CommissionBreakdown = {
  cpPercentage: number;
  cpAmount: number;
  agentRate: number;
  agentAmount: number;
};

export type CpCommissionRow = {
  bookingId: string;
  channelPartnerId: string;
  bookingDate?: string;
  buyerName?: string;
  unitNumber?: string;
  agreementValue: number;
  cpPercentage: number;
  cpAmount: number;
  payoutStatus: PayoutStatus;
};

export interface Booking {
  id: string;
  deal_id?: string | number | null;
  contact_id: string | number;
  /** Null after cancellation so the unit can be rebooked (unique property_id). */
  property_id: string | null;
  booking_amount: number;
  agreement_value: number;
  payment_plan: PaymentPlan;
  booking_date: string;
  possession_date?: string;
  documents_verified: boolean;
  list_price?: number | null;
  discount_amount?: number;
  discount_status?: DiscountStatus;
  discount_approved_by?: string | number | null;
  cp_commission_amount?: number;
  agent_commission_amount?: number;
  cp_payout_status?: PayoutStatus;
  agent_payout_status?: PayoutStatus;
  channel_partner_id?: string | null;
  cp_percentage?: number | null;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
  cancelled_property_id?: string | null;
  amount_paid_total?: number | null;
  deduction_percent?: number | null;
  deduction_amount?: number | null;
  refund_amount?: number | null;
  cancelled_by?: string | number | null;
  created_at: string;
}

export interface PaymentSchedule {
  id: string;
  booking_id: string;
  milestone_name: string;
  due_percentage: number;
  amount_due: number;
  amount_paid: number;
  due_date?: string;
  status: "PENDING" | "DEMAND_SENT" | "PAID" | "OVERDUE" | "CANCELLED";
  created_at: string;
}

export interface CostSheet {
  id: string;
  property_id: string;
  base_rate_sqft: number;
  plc_charges: number;
  car_parking_charges: number;
  club_membership_charges: number;
  gst_percentage: number;
  stamp_duty_percentage: number;
  registration_charges: number;
  total_agreement_value: number;
  created_at: string;
}

export type TransitionUnitStatusOptions = {
  contactId?: string | number;
  holdUntilIso?: string;
  dealId?: string | number;
  skipDealSync?: boolean;
  /** Internal: only cancelBooking may release Booked → Available. */
  allowBookingCancel?: boolean;
};

export type TransitionUnitStatusResult = {
  ok: boolean;
  error?: string;
  property?: Property;
};

export type CreateBookingInput = {
  contactId: string | number;
  propertyId: string;
  agreementValue?: number;
  listPrice?: number;
  discountAmount?: number;
  dealId?: string | number;
  buyerName?: string;
};

export type CreateBookingResult = {
  ok: boolean;
  error?: string;
  bookingId?: string;
};

export type UnitHoldResult = {
  ok: boolean;
  error?: string;
  property?: Property;
};
